import { createContext, useContext, useState, useCallback, useRef } from 'react';
import type { CountdownItem } from '@/components/base/CountdownFloat';

interface CountdownContextType {
  countdownItems: CountdownItem[];
  addCountdown: (item: Omit<CountdownItem, 'id' | 'startedAt'> & { id?: string }) => void;
  removeCountdown: (id: string) => void;
  finishCountdown: (id: string) => void;
}

const CountdownContext = createContext<CountdownContextType | null>(null);

export function CountdownProvider({ children }: { children: React.ReactNode }) {
  const [countdownItems, setCountdownItems] = useState<CountdownItem[]>([]);

  const addCountdown = useCallback((item: Omit<CountdownItem, 'id' | 'startedAt'> & { id?: string }) => {
    const newItem: CountdownItem = {
      id: item.id || `countdown-${Date.now()}`,
      childName: item.childName,
      itemName: item.itemName,
      totalSeconds: item.totalSeconds,
      remainingSeconds: item.remainingSeconds,
      startedAt: Date.now(),
    };
    setCountdownItems(prev => [...prev, newItem]);
  }, []);

  const removeCountdown = useCallback((id: string) => {
    setCountdownItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const finishCountdown = useCallback((id: string) => {
    setCountdownItems(prev => prev.map(item =>
      item.id === id ? { ...item, remainingSeconds: 0 } : item
    ));
  }, []);

  return (
    <CountdownContext.Provider value={{ countdownItems, addCountdown, removeCountdown, finishCountdown }}>
      {children}
    </CountdownContext.Provider>
  );
}

export function useCountdown() {
  const ctx = useContext(CountdownContext);
  if (!ctx) throw new Error('useCountdown must be used within CountdownProvider');
  return ctx;
}
